import {
  Autocomplete,
  Box,
  Button,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { handleToastMsg } from "../../components/Toast";
import Loader from "../../components/Loader";
import TicketHistory from "./TicketHistory";
import PreviewDialog from "./PreviewDialog";
import {
  createVariationLimit,
  deleteVariationLimit,
  getVariationFields,
  getVariationLimits,
  getVariationSettings,
  getVariationUsers,
  previewVariationCheck,
  runVariationCheck,
  updateVariationEnabled,
  updateVariationLimit,
  updateVariationSettings,
} from "./mutation";

const LIMITS_KEY = "production-variation-limits";
const SETTINGS_KEY = "production-variation-settings";
const SHIFTS = [1, 2, 3];

// The server's reason for a failed save, else a generic message
const errorMessage = (error) => {
  const data = error?.response?.data;
  const reason = [data?.message, data?.error].filter(Boolean).join(": ");
  return reason || "Data Error Occurred";
};

// Same rule as the server: case, spaces and punctuation ignored, % = PCT
const fieldKey = (name) =>
  String(name ?? "")
    .toUpperCase()
    .replace(/%/g, "PCT")
    .replace(/[^A-Z0-9]/g, "");

const userLabel = (user) =>
  user ? `${user.name ?? "-"} (${user.employeeCode ?? "-"})` : "";

const blankToNull = (value) => (value === "" || value === null ? null : value);

const EMPTY_LIMIT = { field: "", minValue: "", maxValue: "", isActive: true };

const today = () => {
  const now = new Date();
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
  return now.toISOString().slice(0, 10);
};

function ProductionVariation() {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState(0);
  const { data: fields = [] } = useQuery(
    "production-variation-fields",
    getVariationFields
  );
  const { data: users = [] } = useQuery(
    "production-variation-users",
    getVariationUsers
  );
  const { data: limits = [], isLoading } = useQuery(
    LIMITS_KEY,
    getVariationLimits
  );
  const { data: settings } = useQuery(SETTINGS_KEY, getVariationSettings);

  // Ticket settings form
  const [reporterId, setReporterId] = useState(null);
  const [managerIds, setManagerIds] = useState([]);
  useEffect(() => {
    if (!settings) return;
    setReporterId(settings.reportedByUserId ?? null);
    setManagerIds(settings.managerUserIds ?? []);
  }, [settings]);

  // New limit form, and unsaved min / max edits by limit id
  const [newLimit, setNewLimit] = useState(EMPTY_LIMIT);
  const [edits, setEdits] = useState({});

  // Run check form
  const [runDate, setRunDate] = useState(today());
  const [runShift, setRunShift] = useState(1);

  const usersById = useMemo(
    () => new Map(users.map((user) => [user.id, user])),
    [users]
  );
  const unusedFields = useMemo(() => {
    const used = new Set(limits.map((limit) => fieldKey(limit.field)));
    return fields.filter((field) => !used.has(fieldKey(field)));
  }, [fields, limits]);

  const refreshLimits = () => queryClient.invalidateQueries(LIMITS_KEY);
  const onError = (error) => handleToastMsg(errorMessage(error), "error");

  const saveSettings = useMutation(updateVariationSettings, {
    onSuccess: () => {
      handleToastMsg("Ticket settings saved", "success");
      queryClient.invalidateQueries(SETTINGS_KEY);
    },
    onError,
  });

  const toggleEnabled = useMutation(updateVariationEnabled, {
    onSuccess: ({ enabled }) => {
      handleToastMsg(
        `Automatic tickets ${enabled ? "on" : "off"}`,
        enabled ? "success" : "warning"
      );
      queryClient.invalidateQueries(SETTINGS_KEY);
    },
    onError,
  });

  const addLimit = useMutation(createVariationLimit, {
    onSuccess: () => {
      handleToastMsg("Field added", "success");
      setNewLimit(EMPTY_LIMIT);
      refreshLimits();
    },
    onError,
  });

  const updateLimit = useMutation(updateVariationLimit, {
    onSuccess: (limit) => {
      setEdits(({ [limit.id]: _saved, ...rest }) => rest);
      refreshLimits();
    },
    onError,
  });

  const removeLimit = useMutation(deleteVariationLimit, {
    onSuccess: () => {
      handleToastMsg("Field removed", "success");
      refreshLimits();
    },
    onError,
  });

  const runCheck = useMutation(runVariationCheck, {
    onSuccess: (result) =>
      handleToastMsg(
        result?.skipped
          ? `Not checked: ${result.skipped}`
          : `${result?.rows ?? 0} rows checked, ${result?.tickets ?? 0} tickets raised`,
        result?.skipped ? "warning" : "success"
      ),
    onError,
  });

  // Dry run; the dialog shows the result for previewTarget
  const [previewTarget, setPreviewTarget] = useState(null);
  const previewCheck = useMutation(previewVariationCheck, {
    onError: (error) => {
      setPreviewTarget(null);
      onError(error);
    },
  });

  const handlePreview = () => {
    const target = { date: runDate, shift: runShift };
    setPreviewTarget(target);
    previewCheck.mutate(target);
  };

  const handleRunCheck = () => {
    if (
      window.confirm(
        `Create tickets for ${runDate} Shift ${runShift}? Managers will be alerted (and sent SMS if enabled).`
      )
    ) {
      runCheck.mutate({ date: runDate, shift: runShift });
    }
  };

  const editValue = (limit, key) =>
    edits[limit.id]?.[key] ?? (limit[key] === null ? "" : String(limit[key]));

  const setEdit = (limit, key, value) =>
    setEdits((current) => ({
      ...current,
      [limit.id]: { ...current[limit.id], [key]: value },
    }));

  const handleAdd = () => {
    if (!newLimit.field.trim()) {
      handleToastMsg("Select a field", "error");
      return;
    }
    addLimit.mutate({
      field: newLimit.field,
      minValue: blankToNull(newLimit.minValue),
      maxValue: blankToNull(newLimit.maxValue),
      isActive: newLimit.isActive,
    });
  };

  const handleSaveLimit = (limit) =>
    updateLimit.mutate(
      {
        id: limit.id,
        payload: {
          minValue: blankToNull(editValue(limit, "minValue")),
          maxValue: blankToNull(editValue(limit, "maxValue")),
        },
      },
      { onSuccess: () => handleToastMsg(`${limit.field} saved`, "success") }
    );

  const handleToggle = (limit, isActive) =>
    updateLimit.mutate(
      { id: limit.id, payload: { isActive } },
      {
        onSuccess: () =>
          handleToastMsg(
            `${limit.field} ${isActive ? "active" : "inactive"}`,
            "success"
          ),
      }
    );

  const handleDelete = (limit) => {
    if (window.confirm(`Remove the limit for ${limit.field}?`)) {
      removeLimit.mutate(limit.id);
    }
  };

  return (
    <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Production variation tickets
          </Typography>
          <Typography variant="body2" color="text.secondary">
            30 minutes after each shift ends, the shift's production figures
            are checked against these limits. Each machine with an active field
            outside its min / max gets one ticket.
          </Typography>
        </Box>
        <FormControlLabel
          labelPlacement="start"
          label={
            <Typography fontWeight={600}>
              Automatic tickets {settings?.enabled === false ? "OFF" : "ON"}
            </Typography>
          }
          control={
            <Switch
              color="success"
              checked={settings?.enabled !== false}
              disabled={!settings || toggleEnabled.isLoading}
              onChange={(event) => toggleEnabled.mutate(event.target.checked)}
            />
          }
        />
      </Box>

      <Tabs value={tab} onChange={(_, next) => setTab(next)}>
        <Tab label="Settings" />
        <Tab label="Ticket history" />
      </Tabs>

      {tab === 1 && <TicketHistory />}

      {tab === 0 && (
        <>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Ticket settings
            </Typography>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
              <Autocomplete
                sx={{ minWidth: 280, flex: 1 }}
                options={users}
                getOptionLabel={userLabel}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={usersById.get(reporterId) ?? null}
                onChange={(_, user) => setReporterId(user?.id ?? null)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="Raise tickets as"
                    helperText={
                      settings?.isDefault
                        ? `Default: employee ${settings.defaultEmployeeCode}`
                        : " "
                    }
                  />
                )}
              />
              <Autocomplete
                multiple
                sx={{ minWidth: 320, flex: 2 }}
                options={users}
                getOptionLabel={userLabel}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={managerIds.map((id) => usersById.get(id)).filter(Boolean)}
                onChange={(_, selected) => setManagerIds(selected.map((user) => user.id))}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="Assign tickets to"
                    helperText=" "
                  />
                )}
              />
              <Button
                variant="contained"
                sx={{ mb: 2.5 }}
                disabled={saveSettings.isLoading}
                onClick={() =>
                  saveSettings.mutate({
                    reportedByUserId: reporterId,
                    managerUserIds: managerIds,
                  })
                }
              >
                Save
              </Button>
            </Box>
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Field limits
            </Typography>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center", mb: 2 }}>
              <Autocomplete
                freeSolo
                sx={{ minWidth: 220 }}
                options={unusedFields}
                inputValue={newLimit.field}
                onInputChange={(_, field) => setNewLimit((current) => ({ ...current, field }))}
                renderInput={(params) => <TextField {...params} size="small" label="Field" />}
              />
              <TextField
                size="small"
                type="number"
                label="Min"
                value={newLimit.minValue}
                onChange={(event) =>
                  setNewLimit((current) => ({ ...current, minValue: event.target.value }))
                }
              />
              <TextField
                size="small"
                type="number"
                label="Max"
                value={newLimit.maxValue}
                onChange={(event) =>
                  setNewLimit((current) => ({ ...current, maxValue: event.target.value }))
                }
              />
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Switch
                  checked={newLimit.isActive}
                  onChange={(event) =>
                    setNewLimit((current) => ({ ...current, isActive: event.target.checked }))
                  }
                />
                <Typography variant="body2">
                  {newLimit.isActive ? "Active" : "Inactive"}
                </Typography>
              </Box>
              <Button variant="contained" onClick={handleAdd} disabled={addLimit.isLoading}>
                Add field
              </Button>
            </Box>

            {isLoading ? (
              <Loader />
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Field</TableCell>
                      <TableCell>Min</TableCell>
                      <TableCell>Max</TableCell>
                      <TableCell>Active</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {limits.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          No fields added yet: no tickets will be raised
                        </TableCell>
                      </TableRow>
                    )}
                    {limits.map((limit) => (
                      <TableRow key={limit.id} sx={{ opacity: limit.isActive ? 1 : 0.6 }}>
                        <TableCell>{limit.field}</TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={editValue(limit, "minValue")}
                            onChange={(event) => setEdit(limit, "minValue", event.target.value)}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={editValue(limit, "maxValue")}
                            onChange={(event) => setEdit(limit, "maxValue", event.target.value)}
                          />
                        </TableCell>
                        <TableCell>
                          <Switch
                            checked={limit.isActive}
                            onChange={(event) => handleToggle(limit, event.target.checked)}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Save min / max">
                            <span>
                              <IconButton
                                color="primary"
                                disabled={!edits[limit.id]}
                                onClick={() => handleSaveLimit(limit)}
                              >
                                <SaveIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Remove">
                            <IconButton color="error" onClick={() => handleDelete(limit)}>
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Check a shift now
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Preview lists what would be ticketed without creating anything.
              Run check creates the tickets (machines already ticketed for that
              shift are skipped).
            </Typography>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
              <TextField
                size="small"
                type="date"
                label="Shift date"
                InputLabelProps={{ shrink: true }}
                value={runDate}
                onChange={(event) => setRunDate(event.target.value)}
              />
              <TextField
                select
                size="small"
                label="Shift"
                sx={{ minWidth: 120 }}
                value={runShift}
                onChange={(event) => setRunShift(Number(event.target.value))}
              >
                {SHIFTS.map((shift) => (
                  <MenuItem key={shift} value={shift}>
                    Shift {shift}
                  </MenuItem>
                ))}
              </TextField>
              <Button
                variant="outlined"
                disabled={previewCheck.isLoading}
                onClick={handlePreview}
              >
                Preview
              </Button>
              <Button
                variant="contained"
                disabled={runCheck.isLoading}
                onClick={handleRunCheck}
              >
                {runCheck.isLoading ? "Checking..." : "Run check"}
              </Button>
            </Box>
          </Paper>
        </>
      )}

      <PreviewDialog
        open={Boolean(previewTarget)}
        onClose={() => setPreviewTarget(null)}
        loading={previewCheck.isLoading}
        result={previewCheck.data}
        date={previewTarget?.date}
        shift={previewTarget?.shift}
      />
    </Box>
  );
}

export default ProductionVariation;
